import LogoHorizontal from "./assets/images/123F-Logo-Horizontal.svg?react";
import Logo from "./assets/images/123F-Logo-Stacked.svg?react";
import LogoLink from "./assets/images/123F-Logo-Stacked.svg";
import LifestyleRoseLily from "./assets/images/lifestyles/123f-post-valentines-2025.jpg";
import { getApiUrlFromRequest } from "./utilities/requestContext";

const isBrowser = typeof window !== "undefined" && typeof window.location !== "undefined";
const envHost = typeof process !== "undefined" && process.env ? process.env.HOST_URL : undefined;
const hostname = isBrowser ? window.location.hostname : (envHost ?? "www.123-flowers.co.uk");
const origin = isBrowser ? window.location.origin : `https://${hostname}`;
const currentHref = isBrowser ? window.location.href : origin;
const isDev = import.meta.env.DEV ?? (typeof process !== "undefined" && process.env?.NODE_ENV !== "production");
const isLocal = hostname.includes(".local") || hostname.includes("localhost") || hostname.includes("127.0.0.1");
const isStaging = hostname.includes("staging.");

// Dynamic API URL that resolves at runtime
function getApiUrl(): string {
	// On server-side, check request context first (set by initServerSession)

	if (!isBrowser) {
		let requestApiUrl = getApiUrlFromRequest();

		if (requestApiUrl) {
			// THE FIX: Intercept the requested URL. If it is local, force it to standard HTTP
			// so workerd doesn't crash on MAMP's self-signed SSL certificate.
			if (requestApiUrl.includes(".local") || requestApiUrl.includes("localhost") || requestApiUrl.includes("127.0.0.1")) {
				requestApiUrl = requestApiUrl.replace("https://", "http://");
			}

			return requestApiUrl;
		}
	}

	const currentHostname = hostname;
	const currentIsDev = import.meta.env.DEV ?? (typeof process !== "undefined" && process.env?.NODE_ENV !== "production");
	const currentIsStaging = currentHostname.includes("staging.");
	const currentIsLocal = currentHostname.includes(".local") || currentHostname.includes("localhost") || currentHostname.includes("127.0.0.1");

	if (currentIsDev || currentIsLocal) {
		if (!isBrowser) {
			// 1. SSR Server (workerd): Use HTTP to bypass strict SSL errors
			return "https://www.123-flowers.local/api";
		} else {
			// 2. Client Browser: Use HTTPS because the browser accepted the certificate, and avoids Mixed Content errors
			return "https://www.123-flowers.local/api";
		}
	} else if (currentIsStaging) {
		return "https://staging.123-flowers.co.uk/api";
	}
	return "https://www.123-flowers.co.uk/api";
}

interface Config {
	apiUrl: string;
	baseUrl: string;
	currentUrl: string;
	liveUrl: string;
	isDev: boolean;
	isStaging: boolean;
	isLocal: boolean;
	CLOUDINARY_OVERRIDE: boolean;
	cloudinaryUrl: string;
	cloudinaryVideoUrl: string;
	personalisedThumbUrl: string;
	reCaptchaKey: string;
	apiVersion: string;
	personalisedCardPrice: number;
	personalisedCardEnabled: boolean;
	personalisedCardCutoff: Record<number, number>;
	Logo: React.FC<React.SVGProps<SVGSVGElement>>;
	siteName: string;
	csEmail: string;
	orderPrefix: string;
	loadingAnimation: boolean;
	paymentMethodsEnabled: {
		paypalEnabled: boolean;
		applePayEnabled: boolean;
		googlePayEnabled: boolean;
		clearpayEnabled: boolean;
		klarnaEnabled: boolean;
	};
	currency: string;
	referralVoucherAmount: number;
	meta: {
		title: string;
		description: string;
		structuredData: any[];
	};
	cacheTimes: {
		homepage: number;
		category: number;
		product: number;
	};
	cacheVersion: string;
	socials: {
		facebook: string;
		instagram: string;
		twitter: string;
		tiktok: string;
		pinterest: string;
	};
	recurringPaymentOptions: string[];
	addressApiToken: string;
	SITE_ID: number;
	GTM_ANALYTICS_ID: string;
	GTM_FB_PIXEL_ID: string;
	GTM_TIKTOK_PIXEL_ID: string;
	GTM_BING_TAG_ID: string;
	GTM_ADWORDS_CONVERSION_ID: string;
	GTM_ADWORDS_CONVERSION_LABEL: string;
	GTM_MOUSEFLOW_ID: string;
	GTM_AWIN_MERCHANT_ID: string;
	GTM_KELKOO_ID: string;
	CLARITY_ID: string;
	POSTHOG_KEY: string;
	POSTHOG_API: string;
	TAWK_TO_KEY: string;
	NEW_RELIC_ACCOUNT_ID: string;
	NEW_RELIC_AGENT_ID: string;
	NEW_RELIC_APP_ID: string;
	NEW_RELIC_LICENSE_KEY: string;
	MEILISEARCH_HOST: string;
	MEILISEARCH_API_KEY: string;
	PAYPAL_CLIENT_ID?: string;
	KLARNA_SDK_KEY?: string;
	KLARNA_ENV?: string;
	title: (pageTitle: string) => string;
}

const url = {
	get apiUrl() {
		return getApiUrl();
	},
	baseUrl: origin,
	currentUrl: currentHref,
	liveUrl: "https://www.123-flowers.co.uk",
};

const CLOUDINARY_OVERRIDE = true;

const config: Config = {
	...url,
	isDev,
	CLOUDINARY_OVERRIDE,
	cloudinaryUrl: CLOUDINARY_OVERRIDE ? url.liveUrl.replace("www.", "cf-images.") + "/sharpen=0.8,quality=85,dpr=1,format=auto" : url.liveUrl.replace("www.", "images.") + "/fetch/e_sharpen:80,q_auto,dpr_auto,f_auto",
	isStaging: isStaging,
	isLocal: isLocal,
	cloudinaryVideoUrl: url.liveUrl.replace("www.", "videos.") + "/fetch",
	// Use getter to resolve API URL dynamically at runtime
	get apiUrl() {
		return getApiUrl();
	},
	// apiUrl: "https://localhost.123-flowers.co.uk/api",
	personalisedThumbUrl: "https://prestige-gifting-pw.s3-eu-west-1.amazonaws.com",
	reCaptchaKey: "6Ld03KkZAAAAAKX3wLeKju3xy912TjlLIgB0Dw0C",
	apiVersion: "v1",
	personalisedCardPrice: 3.99,
	personalisedCardEnabled: true,
	personalisedCardCutoff: {
		0: 0,
		1: 18,
		2: 18,
		3: 18,
		4: 18,
		5: new Date() > new Date("2025-03-29") ? 18 : 0, //6pm unless before 29th March 2025
		6: 16,
	},
	Logo: 1 === 2 ? Logo : LogoHorizontal,
	siteName: "123 Flowers",
	csEmail: "enquiries@123-flowers.co.uk",
	orderPrefix: "123_",
	loadingAnimation: false,
	paymentMethodsEnabled: {
		paypalEnabled: true,
		applePayEnabled: true,
		googlePayEnabled: true,
		clearpayEnabled: true,
		klarnaEnabled: true,
	},
	currency: "£",
	referralVoucherAmount: 7.5,
	meta: {
		//these base ones need adding to the index.html as well
		title: "123 Flowers | Flower Delivery as Easy as 123 | Guaranteed Fresh Flowers",
		description: "Flower Delivery has never been easier. Premium Flowers, no hassle timely delivery | 123 Flowers",
		structuredData: [],
	},
	cacheTimes: {
		homepage: 2,
		category: 2,
		product: 2,
	},
	cacheVersion: "{COMMIT_VERSION}",
	socials: {
		facebook: "https://www.facebook.com/123flowersUK",
		instagram: "https://www.instagram.com/123flowersuk",
		twitter: "https://www.x.com/123Flowers_",
		tiktok: "https://www.tiktok.com/@123flowers_uk",
		pinterest: "https://www.pinterest.com/123FlowersUK",
	},
	recurringPaymentOptions: ["weekly", "fortnightly", "monthly"],
	addressApiToken: isLocal
		? "dtoken_hEDzcyiWMr2rzFFnidSd1UXQGKiEtS8vRrMv6MkqFjQR_ccP7tK8tD9m7ANJoOMCTldFAk9PJqHEquLz1R5_REZp7sOXBwqzjuF7YQ6uNY4DKzMxArf-oxWPlvr6VY8vMzK_R3oI2BK9kxRlt9mtuAXmUCpbOjcBIVBJfruljoqKfOxRlTLF36fO3K2bYj8QQYhslDZt3e3s6GUbl76c6w"
		: isStaging
			? "dtoken_hEDzcyiWMr2rzFFnidSd1UXQGKiEtS8vRrMv6MkqFjQR_ccP7tK8tD9m7ANJoOMCjzj3TFSKeoygNENw9R8R4cSq4vPVHn9EcJNtHsNbk5qO4XthDq41jgMrMzECt_6jFY-W-vpVjy8zMr9HegjYEr2TFGW32a24BeZQKls6NwEhUEl-u6WOisB7QyLFNYJLMGdJRgNy0JhBiGyUNm3d7ezoZRuXvpzr "
			: "dtoken_hEDzcyiWMr2rzFFnidSd1UXQGKiEtS8vRrMv6MkqFjQR_ccP7tK8tD9m7ANJoOMCTldFAk9PJqHEquLz1R5_RHCTbR7DW5OajuF7YQ6uNY4DKzMxArf-oxWPlvr6VY8vMzK_R3oI2BK9kxRlt9mtuAXmUCpbOjcBiMb7GtNwJTpNgIy4jvdRhWsrilMwKQP161F5v4qlyJ8",
	SITE_ID: 11,
	GTM_ANALYTICS_ID: "UA-101122052-1",
	GTM_FB_PIXEL_ID: "1697484653615530",
	GTM_TIKTOK_PIXEL_ID: "CEVCOPBC77U5M6RIAJKG",
	GTM_BING_TAG_ID: "5708264",
	GTM_ADWORDS_CONVERSION_ID: "998763479",
	GTM_ADWORDS_CONVERSION_LABEL: "ceHcCOnYxQMQ19ef3AM",
	GTM_MOUSEFLOW_ID: "8a949406-1521-422b-9135-72df92e0bb22",
	GTM_AWIN_MERCHANT_ID: "16223",
	GTM_KELKOO_ID: "100499465",
	CLARITY_ID: "9mon4gj0yk",
	POSTHOG_KEY: "phc_SVrsdMdwSTC5ETuMDa3lkAIPNt9qT4NX7Ib269vgHJl",
	POSTHOG_API: "https://eu.posthog.com",
	TAWK_TO_KEY: "5b6afad3e21878736ba2bda9",
	NEW_RELIC_ACCOUNT_ID: "2882525",
	NEW_RELIC_AGENT_ID: "102350894",
	NEW_RELIC_APP_ID: "102344721",
	NEW_RELIC_LICENSE_KEY: "NRJS-28208b77ecafaffd935",
	MEILISEARCH_HOST: "https://ms-c07db018672e-22850.lon.meilisearch.io",
	MEILISEARCH_API_KEY: "ce380a8d2da10ab9e3bb9e31b24c4a9e2012bca9700cd38726b50a9b598a98f9",
	title: (pageTitle: string) => {
		if (pageTitle.includes(`| ${config.siteName}`)) {
			return pageTitle;
		}
		return pageTitle ? `${pageTitle} | ${config.siteName}` : config.siteName;
	},
};

// We need to add these here because we need to check isStaging and isLocal

((config.PAYPAL_CLIENT_ID = config.isStaging || config.isLocal ? "AZeIFv_YWEULoDoTVWbEB2N5JeWSPJwLplsHLTzMc9spcyxtqNpRhg9X7pi6e9UsuhgGyCgDpUGuFVEF" : "AQiMTdGItrXzDSeJlwKHXYFFKuEubHmQ7Sqc7ZQ2v-Ihx3cNY0fVPX9crjuY0ErPo1Do3OWGndQLODKm"),
	(config.KLARNA_SDK_KEY =
		config.isStaging || config.isLocal ? "klarna_test_client_bXZOSVFUdGxaIT92RDlmRkJEND9BeC9KZSRKZWZEQWcsMzQ1MDgxMzAtZDNhMS00MGE4LTgyZGYtZjRjMWE2YmNmNzZlLDEsejFYY3lFZm50L1Y5a0E0Q2tVSTdaaDl1L0JZd2hQL2J5MnlsVzJhcE5xWT0" : "klarna_live_client_QXB4VHklJG53NSVvWWpMIzRVZ1psNSRQI1NoRSlaYnIsNDMzY2JkNGQtNTE0YS00YzYwLWJhN2UtYjgxYzhmZmY1ZTg5LDEsMjhQM2hmaFFOYlkzUWNCNTRVQ1UzYUNMRUhvK1ZGMjNqZTdabHBwd2hBcz0"));
config.KLARNA_ENV = config.isStaging || config.isLocal ? "playground" : "production";

// In the browser, recompute host-derived values so SSR defaults don't leak prod URLs
if (isBrowser) {
	const browserHost = window.location.hostname;
	const browserOrigin = window.location.origin;
	config.baseUrl = browserOrigin;
	config.currentUrl = window.location.href;
	config.isLocal = browserHost.includes(".local") || browserHost.includes("localhost") || browserHost === "127.0.0.1";
	config.isStaging = browserHost.includes("staging.");
	// apiUrl getter already handles browser detection via getApiUrl()
}

// config.meta.structuredData = [
// 	{
// 		"@context": "http://schema.org",
// 		"@type": "Organization",
// 		name: config.siteName,
// 		url: config.liveUrl,
// 		logo: LogoLink,
// 		sameAs: [config.socials.facebook, config.socials.instagram, config.socials.twitter, config.socials.tiktok, config.socials.pinterest]
// 	},
// 	{
// 		"@context": "http://schema.org",
// 		"@type": "WebSite",
// 		name: config.siteName,
// 		url: config.liveUrl
// 	}
// ];

config.meta.structuredData = [
	{
		"@context": "https://schema.org",
		"@graph": [
			{
				"@type": "Organization",
				"@id": `${config.liveUrl}/#organization`,
				name: config.siteName,
				url: `${config.liveUrl}/`,
				logo: {
					"@type": "ImageObject",
					"@id": `${config.liveUrl}/#logo`,
					url: config.Logo,
				},
				email: config.csEmail,
			},
			{
				"@type": "WebSite",
				"@id": `${config.liveUrl}/#website`,
				url: `${config.liveUrl}/`,
				name: config.siteName,
				inLanguage: "en-GB",
				description: config.meta.description,
				publisher: {
					"@id": `${config.liveUrl}/#organization`,
				},
				potentialAction: {
					"@type": "SearchAction",
					target: {
						"@type": "EntryPoint",
						urlTemplate: `${config.liveUrl}/search/{search_term_string}`,
					},
					"query-input": "required name=search_term_string",
				},
			},
			{
				"@type": "WebPage",
				"@id": `${config.liveUrl}/#webpage`,
				url: `${config.liveUrl}/`,
				name: config.siteName,
				isPartOf: {
					"@id": `${config.liveUrl}/#website`,
				},
				about: {
					"@id": `${config.liveUrl}/#organization`,
				},
				description: config.meta.description,
				inLanguage: "en-GB",
				primaryImageOfPage: {
					"@id": `${config.liveUrl}/#logo`,
				},
			},
			{
				"@type": "ImageObject",
				url: LifestyleRoseLily,
				contentUrl: `${config.liveUrl}/flowers/roses-and-lilies`,
				name: "Roses and Lilies",
				description: "A true classic of beautiful pink Rhodos Roses paired with elegant Stargazer Lilies",
			},
		],
	},
];

export const internationalSubCategories: { name: string; url: string }[] = [
	{ name: "All", url: `` },
	{ name: "Birthday", url: `birthday` },
	{ name: "Anniversary", url: `anniversary` },
	{ name: "Romantic", url: `romantic` },
	{ name: "Luxury", url: `luxury` },
];

export const dates: { currentDate: string; firstDate: string; earlyChristmasDate: string } = {
	currentDate: new Date().toJSON().slice(0, 10),
	firstDate: new Date("2024-11-26").toJSON().slice(0, 10),
	earlyChristmasDate: new Date("2024-12-25").toJSON().slice(0, 10),
};

export default config;
